import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import * as moment from 'moment';
import { get } from 'lodash';
import { CampaignRequestService } from 'app/core/services/campaign-request.service';
import { TranslateService } from '@ngx-translate/core';

interface TestResultPdfDialogData {
    fileUrl: string;
}

@Component({
    selector: 'app-test-result-pdf-dialog',
    template: `
        <mat-dialog-content style="max-height: 100%">
            <iframe
                [src]="safeFileUrl"
                style="border: 0; display: block; height: 90vh; width: 50vw;"
            ></iframe>
        </mat-dialog-content>
        <mat-dialog-actions align="end">
            <button mat-button (click)="dialogRef.close()">Đóng</button>
        </mat-dialog-actions>
    `,
})
export class TestResultPdfDialogComponent {
    safeFileUrl: SafeResourceUrl;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: TestResultPdfDialogData,
        public dialogRef: MatDialogRef<TestResultPdfDialogComponent>,
        private sanitizer: DomSanitizer
    ) {
        this.safeFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            data.fileUrl
        );
    }
}

@Component({
    selector: 'app-test-result-cell',
    template: `
        <span class="avatar-container" *ngIf="type === 'avatar'">
            <img
                class="avatar-img"
                src="{{ imageSrc }}"
                alt="{{ convertedValue }}"
            />
        </span>

        <span *ngIf="isCheckBoxCell()">
            <mat-icon *ngIf="convertedValue" class="active-icon green-600 s-16"
                >check
            </mat-icon>
        </span>

        <span *ngIf="isTextCell()">{{ convertedValue }}</span>

        <span *ngIf="type === 'fullName'" class="name-cell">{{
            convertedValue
        }}</span>

        <span *ngIf="type === 'resultPDFView'">
            <button
                type="button"
                (click)="openTestResultPdf()"
                mat-raised-button
                color="accent"
                [disabled]="isLoadingPdf"
            >
                {{ "DATA_TABLE.resultPDFView" | translate}}
            </button>
        </span>
    `,
    styleUrls: ['./test-result-cell-renderer.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TestResultCellRendererComponent
    implements ICellRendererAngularComp
{
    private readonly textCellTypes = ['age', 'resultTakenTime', 'resultTranslated'];
    private readonly checkBoxCellTypes = ['confirm', 'authorized'];

    constructor(
        private campaignRequestService: CampaignRequestService,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {}

    params!: ICellRendererParams;
    type = '';
    convertedValue: string | boolean = '';
    imageSrc = '';
    roundId = '';
    isLoadingPdf = false;

    agInit(params: ICellRendererParams) {
        this.params = params;
        this.type = get(params, 'colDef.field', '');
        this.isLoadingPdf = !params.data?.hasPdf;
        this.convertedValue = '';
        this.imageSrc = '';
        this.roundId = '';

        switch (this.type) {
            case 'age':
                this.setAgeValue(params.data);
                break;
            case 'avatar':
            case 'fullName':
                this.setIdentityValue(params.data);
                break;
            case 'authorized':
                this.convertedValue = get(params.data, 'authorized', false);
                break;
            case 'confirm':
                this.convertedValue = get(params.data, 'confirm', false);
                break;
            case 'resultTakenTime':
                this.setResultTakenTimeValue(params.data);
                break;
            case 'resultPDFView':
                this.roundId = this.getRoundIdFromParams(params);
                break;
            case 'resultTranslated':
                this.setResultTranslatedValue(params.data);
                break;
            default:
                this.convertedValue = '';
                break;
        }
    }

    isTextCell() {
        return this.textCellTypes.includes(this.type);
    }

    isCheckBoxCell() {
        return this.checkBoxCellTypes.includes(this.type);
    }

    refresh(params: ICellRendererParams) {
        this.params = params;
        return true;
    }

    openTestResultPdf() {
        const candidateId = get(this.params, 'data.id', '');
        const roundId = this.roundId || this.getRoundIdFromParams(this.params);

        if (!candidateId || !roundId || this.isLoadingPdf) {
            return;
        }

        this.isLoadingPdf = true;
        this.campaignRequestService
            .getCandidateTestResultPdf(roundId, candidateId)
            .subscribe(
                (blob) => {
                    const pdfBlob = new Blob([blob], {
                        type: 'application/pdf',
                    });
                    const fileURL = URL.createObjectURL(pdfBlob);

                    const dialogRef = this.dialog.open(
                        TestResultPdfDialogComponent,
                        {
                            maxWidth: '1200px',
                            data: {
                                fileUrl: fileURL,
                            },
                        }
                    );

                    dialogRef.afterClosed().subscribe(() => {
                        URL.revokeObjectURL(fileURL);
                    });

                    this.isLoadingPdf = false;
                },
                (error) => {
                    console.log('Cannot open candidate test result PDF', error);
                    this.isLoadingPdf = false;
                }
            );
    }

    private setAgeValue(data: unknown) {
        const birthday = get(data, 'birthday', 0);
        this.convertedValue =
            new Date().getFullYear() - new Date(birthday).getFullYear() + '';
    }

    private setIdentityValue(data: unknown) {
        this.imageSrc = get(data, 'avatar', '');
        const firstName = get(data, 'firstName', '');
        const lastName = get(data, 'lastName', '');
        this.convertedValue = `${lastName} ${firstName}`;
    }

    private setResultTakenTimeValue(data: unknown) {
        const time = get(data, 'resultTakenTime', 0) || 0;
        this.convertedValue = time
            ? moment(new Date(time)).format('HH:mm DD.MM.YY')
            : '';
    }

    private setResultTranslatedValue(data: unknown) {
        const translationKey = get(data, 'resultTranslated', '');
        if (!translationKey) {
            this.convertedValue = '';
            return;
        }
        
        // Get the instant translated value from TranslateService
        this.convertedValue = this.translateService.instant(translationKey);
    }

    private getRoundIdFromParams(params: ICellRendererParams): string {
        return get(params, 'colDef.roundId', '');
    }
}
